import type { ChecklistAttachment, ChecklistItem, ChecklistWorkflowStatus } from '../types/schema'
import { buildChecklistProofAttachmentUpdates } from './checklistProofService'

export interface ProofSubmissionEffectInput {
  item: Pick<
    ChecklistItem,
    'workflowStatus' | 'previousWorkflowStatus' | 'proofApproverId' | 'requiresProofFile'
  >
  attachment: ChecklistAttachment
}

export interface ProofSubmissionEffectResult {
  fieldUpdates: Record<string, unknown>
  enteredAssessing: boolean
}

/**
 * Single choke-point for every proof-attach writer (console drawer, helper
 * offline outbox, member mobile, Field Pass). Returns the merged field patch
 * to apply to the checklist item document.
 *
 * When `proofApproverId` is set on the item, uploading proof flips the item
 * into ASSESSING and marks proofReviewStatus=PENDING — the approver must
 * sign off before COMPLETED. When no approver is set the legacy fast path
 * is preserved (the existing writer's own completion logic decides whether
 * to auto-complete).
 */
export const buildProofSubmissionEffects = (
  input: ProofSubmissionEffectInput,
): ProofSubmissionEffectResult => {
  const attachmentUpdates = buildChecklistProofAttachmentUpdates(input.attachment)
  const hasApprover = Boolean(input.item.proofApproverId)
  if (!hasApprover) {
    return {
      fieldUpdates: {
        ...attachmentUpdates,
        proofReviewStatus: null,
        proofReviewedAt: null,
        proofReviewedById: null,
        proofReviewedByName: null,
        proofRejectionNote: null,
      },
      enteredAssessing: false,
    }
  }
  const current = (input.item.workflowStatus ?? 'ONGOING') as ChecklistWorkflowStatus
  const previous = current !== 'ASSESSING' && current !== 'COMPLETED' ? current : (input.item.previousWorkflowStatus ?? 'ONGOING')
  return {
    fieldUpdates: {
      ...attachmentUpdates,
      workflowStatus: 'ASSESSING',
      previousWorkflowStatus: previous,
      proofReviewStatus: 'PENDING',
      proofReviewedAt: null,
      proofReviewedById: null,
      proofReviewedByName: null,
      proofRejectionNote: null,
      isPendingReview: true,
    },
    enteredAssessing: true,
  }
}

export interface ProofApprovalInput {
  approver: { uid: string; displayName: string }
  now?: string
}

export const buildProofApprovalUpdates = (input: ProofApprovalInput): Record<string, unknown> => ({
  workflowStatus: 'COMPLETED',
  proofReviewStatus: 'APPROVED',
  proofReviewedAt: input.now ?? new Date().toISOString(),
  proofReviewedById: input.approver.uid,
  proofReviewedByName: input.approver.displayName,
  proofRejectionNote: null,
  isPendingReview: false,
})

export interface ProofRejectionInput {
  approver: { uid: string; displayName: string }
  note: string
  item: Pick<ChecklistItem, 'previousWorkflowStatus'>
  now?: string
}

export const buildProofRejectionUpdates = (input: ProofRejectionInput): Record<string, unknown> => {
  const revertTo: ChecklistWorkflowStatus =
    input.item.previousWorkflowStatus && input.item.previousWorkflowStatus !== 'COMPLETED' && input.item.previousWorkflowStatus !== 'ASSESSING'
      ? input.item.previousWorkflowStatus
      : 'ONGOING'
  return {
    workflowStatus: revertTo,
    proofReviewStatus: 'REJECTED',
    proofReviewedAt: input.now ?? new Date().toISOString(),
    proofReviewedById: input.approver.uid,
    proofReviewedByName: input.approver.displayName,
    proofRejectionNote: input.note,
    isPendingReview: false,
  }
}

export interface SubmitRepeatingProofEffectsInput {
  node: Pick<ChecklistItem, 'id' | 'workspaceId' | 'checklistType' | 'boardStatus' | 'recordVersion' | 'notRequiredFrom' | 'occurrences'>
  focusedOccurrenceKey: string | null
  fieldUpdates: Record<string, unknown>
  enteredAssessing: boolean
  actor: { id?: string | null; name?: string | null }
}

export const submitRepeatingProofEffects = async (
  input: SubmitRepeatingProofEffectsInput,
): Promise<void> => {
  const { node, focusedOccurrenceKey, fieldUpdates, enteredAssessing, actor } = input
  const { fetchWorkspaceRawTimezone } = await import('./checklistStatusWriter')
  const { workspaceTodayKey } = await import('./repeat/keys')
  const rawTz = await fetchWorkspaceRawTimezone(node.workspaceId)
  const periodKey = focusedOccurrenceKey ?? workspaceTodayKey(node.checklistType as 'DAILY' | 'WEEKLY', rawTz)
  const proofMetaFields: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(fieldUpdates)) {
    if (!['workflowStatus', 'previousWorkflowStatus', 'isCompleted', 'boardStatus'].includes(key)) {
      proofMetaFields[key] = value
    }
  }
  const { writeRepeatPageEntry } = await import('./repeatPageService')
  await writeRepeatPageEntry({
    checklistId: node.id,
    periodKey,
    fields: proofMetaFields,
  })
  if (enteredAssessing) {
    const { updateChecklistWorkflowStatus } = await import('./checklistStatusWriter')
    const { applyStripboardDelta } = await import('./stripboardDeltaService')
    await updateChecklistWorkflowStatus({
      item: node,
      workflowStatus: 'ASSESSING',
      periodKey,
      actor,
      runDelta: applyStripboardDelta,
    })
  }
}

